import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { cn } from '../../../lib/utils';
import { getKeybindingLayoutMap } from '../dispatch/effectiveKeymap';
import { startRecording } from '../dispatch/installKeybindingDispatcher';
import type { LayoutMapCache } from '../model/layoutMap';
import { platform as currentPlatform } from '../model/platform';
import type { KeybindingCommandId, Platform } from '../model/keybindingTypes';
import { interpretRecorderKeydown, type RecorderRejection } from './recordChord';

export type ChordRecorderCancelReason = 'escape' | 'blur' | 'outside';

export type ChordRecorderProps = {
  readonly commandId: KeybindingCommandId;
  readonly onChord: (chord: string) => void;
  readonly onClear: () => void;
  readonly onCancel: (reason: ChordRecorderCancelReason) => void;
  /** The first refused key of a recording session only; later refusals just update the message. */
  readonly onReject: (reason: RecorderRejection) => void;
  readonly platform?: Platform;
  readonly isDev?: boolean;
  readonly layoutMap?: Pick<LayoutMapCache, 'getSnapshot' | 'subscribe'>;
};

/**
 * The inline "Press keys…" field. Capture happens INSIDE the keybinding
 * dispatcher (plan D3): while this is mounted and the keyboard layout is known,
 * the dispatcher cancels every keydown and hands it here, so recording ⌘W never
 * closes a tab and ⌘Q is reported instead of quitting. Recording ends on
 * commit, Escape, window blur (the dispatcher tells us), a click elsewhere, and
 * unmount — which covers switching sections and closing the Settings dialog.
 */
export function ChordRecorder({
  commandId,
  onChord,
  onClear,
  onCancel,
  onReject,
  platform = currentPlatform(),
  isDev = import.meta.env.DEV,
  layoutMap = getKeybindingLayoutMap(),
}: ChordRecorderProps) {
  const layout = useSyncExternalStore(layoutMap.subscribe, layoutMap.getSnapshot, layoutMap.getSnapshot);
  const [rejection, setRejection] = useState<string | null>(null);
  const rootRef = useRef<HTMLSpanElement>(null);
  const callbacks = useRef({ onChord, onClear, onCancel, onReject });
  callbacks.current = { onChord, onClear, onCancel, onReject };
  const layoutReady = layout.status !== 'loading';

  // Layout effect: capture is live in the same commit that shows "Press keys…",
  // so a key pressed the moment the prompt appears is never run as a command.
  useLayoutEffect(() => {
    if (!layoutReady) return undefined;
    let rejectionReported = false;
    const stop = startRecording((event) => {
      const outcome = interpretRecorderKeydown(event, {
        commandId,
        platform,
        isDev,
        layout: layoutMap.getSnapshot(),
      });
      if (outcome.kind === 'ignore') return;
      if (outcome.kind === 'reject') {
        setRejection(outcome.message);
        if (!rejectionReported) callbacks.current.onReject(outcome.reason);
        rejectionReported = true;
        return;
      }
      stop();
      if (outcome.kind === 'cancel') callbacks.current.onCancel('escape');
      else if (outcome.kind === 'clear') callbacks.current.onClear();
      else callbacks.current.onChord(outcome.chord);
    }, () => callbacks.current.onCancel('blur'));
    return stop;
  }, [commandId, isDev, layoutMap, layoutReady, platform]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      callbacks.current.onCancel('outside');
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, []);

  return (
    <span
      ref={rootRef}
      role="status"
      aria-live="polite"
      className="inline-flex max-w-full flex-col gap-1"
      data-testid={`keybinding-recorder-${commandId}`}
      data-recording={layoutReady ? 'true' : 'false'}
    >
      <span
        className={cn(
          'inline-flex h-7 w-fit items-center whitespace-nowrap rounded-md border border-dashed px-2 text-xs',
          layoutReady
            ? 'border-primary bg-primary/5 text-foreground'
            : 'border-border text-muted-foreground',
        )}
      >
        {layoutReady ? 'Press keys…' : 'Loading keyboard layout…'}
      </span>
      {layoutReady && <span className="text-xs text-muted-foreground">Esc cancels, Backspace clears</span>}
      {layoutReady && platform === 'mac' && !rejection && (
        <span className="text-xs text-muted-foreground">
          Option (⌥) combinations type characters in text fields; they only apply in their scope.
        </span>
      )}
      {rejection && (
        <span className="text-xs text-destructive" data-testid={`keybinding-recorder-message-${commandId}`}>
          {rejection}
        </span>
      )}
    </span>
  );
}
